import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { EmpleadoResult } from 'src/app/domain/model/empleado/empleado';

/**
 * Tabla (dumb component) del listado de "Administrar Empleados/Consultores"
 * (HU1259115). Sin lógica de negocio: solo presentación (frontend-girop §14).
 *
 * <p>Columnas verificadas contra `AdminEmpleadosViewImpl.crearSuraTableEmpleados()`
 * (legado): Tipo/Número Identificación, Nombre/Nombre Comercial, Tipo Empleado
 * y Oficina se muestran como descripción (no código) — igual que la grilla
 * legada — usando los campos `descripcionTipoIdentificacion`/
 * `descripcionTipoEmpleado`/`descripcionOficina` que el backend agrega vía
 * JOIN (ver Administrar-Empleados-Asesores-seguimiento.md §7).</p>
 */
@Component({
  selector: 'app-tabla-empleados',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, TooltipModule, TagModule],
  styleUrls: ['../../../../shared/styles/grid-table.styles.scss'],
  template: `
    <!-- Mobile: cards -->
    <div class="md:hidden space-y-3">
      @for (item of items(); track item.nmconsecutivo) {
        <article class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
          <div class="flex items-start justify-between gap-2 mb-2">
            <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate"
                [attr.title]="tituloTabla(nombreCompleto(item))">
              {{ textoTabla(nombreCompleto(item)) }}
            </h3>
            <p-tag [severity]="item.snactivo === 'S' ? 'success' : 'danger'"
                   [value]="item.snactivo === 'S' ? 'Activo' : 'Inactivo'"></p-tag>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
            <div><span class="text-gray-500 dark:text-gray-400">Tipo identificación:</span> {{ textoTabla(item.descripcionTipoIdentificacion) }}</div>
            <div><span class="text-gray-500 dark:text-gray-400">Número identificación:</span> {{ textoTabla(item.nmidentificacion) }}</div>
            <div><span class="text-gray-500 dark:text-gray-400">Tipo empleado:</span> {{ textoTabla(item.descripcionTipoEmpleado) }}</div>
            <div><span class="text-gray-500 dark:text-gray-400">Oficina:</span> {{ textoTabla(item.descripcionOficina) }}</div>
          </div>
          <p-button label="Editar" icon="pi pi-pencil"
            styleClass="p-button-text w-full min-h-[44px]" (onClick)="editar.emit(item)"></p-button>
        </article>
      } @empty {
        <div class="text-center text-gray-500 dark:text-gray-400 p-6">
          <i class="pi pi-inbox text-4xl mb-3 block"></i>
          No se encontraron empleados con los criterios especificados
        </div>
      }
    </div>

    <!-- Desktop: tabla -->
    <div class="hidden md:block">
      <p-table
        styleClass="p-datatable-sm p-datatable-gridlines tabla-administrar-empleados"
        [value]="items()"
        [scrollable]="true"
        [responsiveLayout]="'scroll'"
        scrollHeight="400px"
        [loading]="loading()"
        [resizableColumns]="true"
        columnResizeMode="expand">
        <ng-template pTemplate="header">
          <tr>
            <th pResizableColumn><span class="grid-header-text" title="Tipo Identificación">Tipo Identificación</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Número Identificación">Número Identificación</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Nombre / Nombre Comercial">Nombre / Nombre Comercial</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Tipo Empleado">Tipo Empleado</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Oficina">Oficina</span></th>
            <th pResizableColumn class="text-center"><span class="grid-header-text" title="Activo">Activo</span></th>
            <th style="width: 6.5rem" class="text-center">Acciones</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
          <tr>
            <td><span class="grid-cell-text" [attr.title]="tituloTabla(item.descripcionTipoIdentificacion)">{{ textoTabla(item.descripcionTipoIdentificacion) }}</span></td>
            <td><span class="grid-cell-text" [attr.title]="tituloTabla(item.nmidentificacion)">{{ textoTabla(item.nmidentificacion) }}</span></td>
            <td><span class="grid-cell-text" [attr.title]="tituloTabla(nombreCompleto(item))">{{ textoTabla(nombreCompleto(item)) }}</span></td>
            <td><span class="grid-cell-text">{{ textoTabla(item.descripcionTipoEmpleado) }}</span></td>
            <td><span class="grid-cell-text">{{ textoTabla(item.descripcionOficina) }}</span></td>
            <td class="text-center">
              <p-tag [severity]="item.snactivo === 'S' ? 'success' : 'danger'"
                     [value]="item.snactivo === 'S' ? 'Activo' : 'Inactivo'"></p-tag>
            </td>
            <td class="text-center">
              <button pButton icon="pi pi-pencil"
                class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Editar" tooltipPosition="left"
                (click)="editar.emit(item)"></button>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7" class="text-center p-6 text-gray-500 dark:text-gray-400">
              <i class="pi pi-inbox text-4xl mb-3 block"></i>
              No se encontraron empleados con los criterios especificados
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <style>
      :host ::ng-deep .tabla-administrar-empleados .p-datatable-table {
        table-layout: fixed;
        width: 100%;
        min-width: 100%;
      }
      :host ::ng-deep .tabla-administrar-empleados .grid-header-text {
        display: inline-block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
        font-weight: bold;
      }
      :host ::ng-deep .tabla-administrar-empleados .grid-cell-text {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        overflow-wrap: anywhere;
      }
    </style>
  `
})
export class TablaEmpleadosComponent {
  readonly items = input.required<EmpleadoResult[]>();
  readonly loading = input(false);

  readonly editar = output<EmpleadoResult>();

  nombreCompleto(item: EmpleadoResult): string {
    if (item.dsnombreComercial?.trim()) return item.dsnombreComercial.trim();
    return [item.dsnombre1, item.dsnombre2, item.dsapellido1, item.dsapellido2]
      .filter((parte) => parte?.trim())
      .join(' ');
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
